import { useEffect, useState } from 'react';

import { RentalManagerPaymentProductType } from '@zg-rentals/ts-rental-manager-api-client';

import { useCharge, useStartPaymentMethodSession } from '../../../hooks/queries/freemium';
import { useGetDefaultPaymentMethod } from '../../../hooks/queries/payment';
import PaymentSelectors from '../../../selectors/PaymentSelectors';
import { useSelector } from '../../../selectors/useSelector';

import { PAYMENT_CONSTANTS } from '../../../constants/PaymentConstants';

import SpinnerContainer from '../../SpinnerContainer';
import CreditCardCapture from '../CreditCardCapture';
import CreditCardCharge from '../CreditCardCharge';
import PaymentError from '../PaymentError';
import SelectPaymentMethod from '../SelectPaymentMethod';

import type { FreemiumChargeResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { FreemiumDatarouterError } from '../../Modal/FreemiumErrorModal';

type Props = {
  listingAlias: string;
  onPaymentMethodChange?: (method: string) => void;
  handleFreemiumChargeSuccess: (response: FreemiumChargeResponseWebDto) => void;
  onFreemiumChargeError: (error: FreemiumDatarouterError) => void;
  isRenderedInPageView: boolean;
  setIsChargeFetching?: (isFetching: boolean) => void;
  currentPhase?: string;
  setHeadingText: (headingText: string) => void;
};

const PaymentDetails = ({
  // listingAlias must be a prop and not pulled from currentProperty because the mobilePaymentPage reads this from the url rather than redux https://bitbucket.hotterpads.com/projects/HOT/repos/rental-manager-web/pull-requests/7195/overview for some more context
  listingAlias,
  onPaymentMethodChange,
  handleFreemiumChargeSuccess,
  onFreemiumChargeError,
  isRenderedInPageView,
  setIsChargeFetching,
  // unit test props hack
  currentPhase: pCurrentPhase,
  setHeadingText,
}: Props) => {
  const hasError = useSelector(PaymentSelectors.makeHasError);
  const isInMaintenanceMode = useSelector(PaymentSelectors.makeIsInMaintenanceMode);
  const formErrorMessage: string = useSelector(PaymentSelectors.makeFormErrorMessage);
  const errorMessage = useSelector(PaymentSelectors.makeErrorMessage);

  const {
    payment: {
      capture: { isCaptured, metroidAuthToken },
      chargeAndActivateListing: { defaultPaymentMethodSuccess, defaultPaymentMethod, isPaymentValid },
    },
    app: {
      cookieEnabledServices: { hostApp },
    },
  } = useSelector((state) => state);

  const [currentPhase, setCurrentPhase] = useState(pCurrentPhase || '');

  const {
    refetch: refetchCharge,
    isFetching: isChargeFetching,
    data: chargeData,
    error: chargeError,
  } = useCharge({
    listingAlias,
    productType: RentalManagerPaymentProductType.FREEMIUM_PAID,
    hostApp: String(hostApp),
  });

  const { refetch: refetchDefaultPaymentMethod } = useGetDefaultPaymentMethod(listingAlias);

  // errors are handled via redux within the hook
  const { refetch: refetchStartPaymentMethodSession } = useStartPaymentMethodSession(
    { listingAlias, hostApp: String(hostApp) },
    {
      onSuccess: () => refetchDefaultPaymentMethod(),
    },
  );

  /**
   * handles state change for the payment charging state
   * updates the parent component to disable modal closing
   */
  useEffect(() => {
    setIsChargeFetching?.(isChargeFetching);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChargeFetching]);

  useEffect(() => {
    handleCurrentPaymentPhaseChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhase]);

  useEffect(() => {
    if (chargeData) {
      handleFreemiumChargeSuccess(chargeData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargeData]);

  useEffect(() => {
    if (chargeError) {
      onFreemiumChargeError(chargeError as FreemiumDatarouterError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargeError]);

  useEffect(() => {
    if (hasError) {
      if (!formErrorMessage) {
        setCurrentPhase(PAYMENT_CONSTANTS.PAYMENT_PHASE.CAUGHT_ERROR);
      }
    } else if (isCaptured) {
      setCurrentPhase(PAYMENT_CONSTANTS.PAYMENT_PHASE.CREDIT_CARD_CHARGE);
    } else if (defaultPaymentMethod) {
      setCurrentPhase(
        // @ts-expect-error setCurrentPhase is not typed
        defaultPaymentMethod.defaultPaymentMethodExists
          ? PAYMENT_CONSTANTS.PAYMENT_PHASE.SELECT_PAYMENT_METHOD
          : PAYMENT_CONSTANTS.PAYMENT_PHASE.CREDIT_CARD_CAPTURE,
      );
    } else if (defaultPaymentMethodSuccess === false) {
      setCurrentPhase(PAYMENT_CONSTANTS.PAYMENT_PHASE.CREDIT_CARD_CAPTURE);
    }
  }, [hasError, formErrorMessage, isCaptured, defaultPaymentMethod, defaultPaymentMethodSuccess]);

  const handleCurrentPaymentPhaseChange = (): void => {
    if (!metroidAuthToken) {
      refetchStartPaymentMethodSession();
    } else if (currentPhase === PAYMENT_CONSTANTS.PAYMENT_PHASE.CREDIT_CARD_CHARGE) {
      refetchCharge();
    }
  };

  const handlePaymentMethodChange = (method: string): void => {
    if (method === PAYMENT_CONSTANTS.PAYMENT_METHOD.NEW || method === PAYMENT_CONSTANTS.PAYMENT_METHOD.DEFAULT) {
      setCurrentPhase(PAYMENT_CONSTANTS.PAYMENT_PHASE.CREDIT_CARD_CAPTURE);
    } else {
      setCurrentPhase(PAYMENT_CONSTANTS.PAYMENT_PHASE.CREDIT_CARD_CHARGE);
    }
    onPaymentMethodChange?.(method);
  };

  // display select payment modal if defaultPaymentMethod failed or if defaultPaymentMethod succeeded but the user
  // doesn't have a saved card. Otherwise just display CreditCardCapture modal.
  const displaySelectPaymentMethodModal =
    (defaultPaymentMethodSuccess !== null && defaultPaymentMethodSuccess === false) ||
    // @ts-expect-error defaultPaymentMethodSuccess is not typed
    (defaultPaymentMethodSuccess && defaultPaymentMethod?.defaultPaymentMethodExists);

  switch (currentPhase) {
    case PAYMENT_CONSTANTS.PAYMENT_PHASE.SELECT_PAYMENT_METHOD:
    case PAYMENT_CONSTANTS.PAYMENT_PHASE.CREDIT_CARD_CAPTURE:
      return (
        <SelectPaymentMethod
          onPaymentMethodChange={handlePaymentMethodChange}
          setCurrentPhase={setCurrentPhase}
          displaySelectPaymentMethodModal={displaySelectPaymentMethodModal}
          isRenderedInPageView={isRenderedInPageView}
          creditCardCapture={
            <CreditCardCapture
              formErrorMessage={formErrorMessage}
              displayedInSelectPaymentMethodModal={displaySelectPaymentMethodModal}
            />
          }
        />
      );
    case PAYMENT_CONSTANTS.PAYMENT_PHASE.CREDIT_CARD_CHARGE:
      return <CreditCardCharge isPaymentValid={isPaymentValid} setHeadingText={setHeadingText} />;
    case PAYMENT_CONSTANTS.PAYMENT_PHASE.CAUGHT_ERROR:
      return <PaymentError isInMaintenanceMode={isInMaintenanceMode} errorMessage={errorMessage} />;
    default:
      return <SpinnerContainer />;
  }
};

export default PaymentDetails;
